import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LiquidGlassToggle from './Toggle';

const variants = [
  'primary',
  'secondary',
  'success',
  'danger',
  'warning',
  'info',
  'purple',
];
const sizes = ['small', 'medium', 'large', 'xlarge'];

const ToggleDemo = () => {
  const [toggles, setToggles] = useState({
    primary: true,
    secondary: false,
    success: true,
    danger: false,
    warning: true,
    info: false,
    purple: true,
  });
  const [size, setSize] = useState('medium');
  const [disabled, setDisabled] = useState(false);

  const handleToggle = (variant, value) => {
    setToggles((prev) => ({ ...prev, [variant]: value }));
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        linear-gradient(135deg, #667eea 0%, #764ba2 100%),
        radial-gradient(circle at 20% 80%, #f093fb 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, #f5576c 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, #4facfe 0%, transparent 50%)
      `,
      backgroundBlendMode: 'overlay, screen, screen, screen',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{
          textAlign: 'center',
          color: 'white',
          marginBottom: '20px'
        }}
      >
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          margin: '0 0 10px 0',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(45deg, #fff, #f0f0f0)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Liquid Glass Toggle
        </h1>
        <p style={{
          fontSize: '1.2rem',
          opacity: 0.9,
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Beautiful, animated glass morphism toggle switches
        </p>
      </motion.div>

      {/* Size Selector */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ color: 'white', fontWeight: 500 }}>Size:</span>
        {sizes.map((s) => (
          <button
            key={s}
            onClick={() => setSize(s)}
            style={{
              background: size === s ? 'rgba(255,255,255,0.2)' : 'transparent',
              border: 'none',
              borderRadius: '8px',
              padding: '6px 16px',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '1rem',
              transition: 'all 0.2s',
              outline: size === s ? '2px solid #fff' : 'none',
              boxShadow: size === s ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <span style={{ color: 'white', fontWeight: 500, marginLeft: 24 }}>Disabled:</span>
        <input
          type="checkbox"
          checked={disabled}
          onChange={e => setDisabled(e.target.checked)}
          style={{ width: 20, height: 20 }}
        />
      </div>

      {/* Variants Showcase */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '32px',
          width: '100%',
          maxWidth: '1200px',
        }}
      >
        {variants.map((variant) => (
          <div key={variant} style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
            <LiquidGlassToggle
              checked={toggles[variant]}
              onChange={val => handleToggle(variant, val)}
              variant={variant}
              size={size}
              label={variant.charAt(0).toUpperCase() + variant.slice(1)}
              description={`This is a ${variant} toggle.`}
              disabled={disabled}
              animated
              showIcon
            />
          </div>
        ))}
      </motion.div>

      {/* Interactive Example */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          marginTop: 40,
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: 32,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          maxWidth: 600,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24
        }}
      >
        <h2 style={{ color: 'white', margin: 0, fontSize: '1.5rem' }}>Interactive Example</h2>
        <LiquidGlassToggle
          checked={toggles.primary}
          onChange={val => handleToggle('primary', val)}
          variant="primary"
          size={size}
          label="Enable Notifications"
          description="Toggle to enable or disable notifications."
          animated
          showIcon
        />
        <div style={{ color: 'white', marginTop: 12, fontSize: '1.1rem' }}>
          Notifications are <b>{toggles.primary ? 'Enabled' : 'Disabled'}</b>
        </div>
      </motion.div>

      {/* Features List */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '1200px',
          marginTop: 40
        }}
      >
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Toggle Features
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Glass Effects</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>backdrop-filter: blur(10-25px)</li>
              <li>Multi-layered gradients</li>
              <li>Glass borders & shadows</li>
              <li>Corner highlights</li>
              <li>Inner glow effects</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Animations</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Thumb parallax & glow</li>
              <li>Ripple & hover effects</li>
              <li>Spring physics</li>
              <li>Icon transitions</li>
              <li>Content lift effects</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Customization</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>7 color variants</li>
              <li>4 size options</li>
              <li>Custom labels & descriptions</li>
              <li>Disabled state</li>
              <li>Flexible styling</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ToggleDemo; 